import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { mainNavigationGuard } from './guards'
import { 
  PERSONAL_CENTER_ROUTES, 
  AUTH_ROUTES, 
  MAIN_ROUTES
} from './types'
import { activityTracker } from '@/utils/activityTracker'

// 直接导入关键页面组件以避免懒加载导致的空白页面
import HomePage from '@/views/HomePage.vue'
import LoginPage from '@/views/auth/LoginPage.vue'
import RegisterPage from '@/views/auth/RegisterPage.vue'
import DashboardPage from '@/views/DashboardPage.vue'
import QuestionnairePage from '@/views/QuestionnairePage.vue'

const routes: RouteRecordRaw[] = [
    {
      path: '/',
      name: MAIN_ROUTES.HOME,
      component: HomePage,
      meta: {
        title: 'Carbon Trading Platform',
        requiresAuth: false,
        description: '碳交易平台首页',
        group: 'main'
      }
    },
    {
      path: '/login',
      name: AUTH_ROUTES.LOGIN,
      component: LoginPage,
      meta: {
        title: 'Login',
        requiresAuth: false,
        redirectIfAuth: false,
        description: '用户登录页面',
        group: 'auth'
      }
    },
    {
      path: '/register',
      name: 'Register',
      component: RegisterPage,
      meta: {
        title: 'Register',
        requiresAuth: false,
        redirectIfAuth: true
      }
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: () => import('@/views/auth/ForgotPasswordPage.vue'),
      meta: {
        title: 'Forgot Password',
        requiresAuth: false,
        redirectIfAuth: true
      }
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/auth/ResetPasswordPage.vue'),
      meta: {
        title: 'Reset Password',
        requiresAuth: false,
        redirectIfAuth: true
      }
    },
    {
      path: '/set-payment-password',
      name: 'SetPaymentPassword',
      component: () => import('@/views/SetPaymentPasswordPage.vue'),
      meta: {
        title: 'Set Withdraw PIN',
        requiresAuth: true
      }
    },
    {
      path: '/questionnaire',
      name: 'Questionnaire',
      component: QuestionnairePage,
      meta: {
        title: 'Carbon Footprint Calculator',
        requiresAuth: false,
        allowWithoutQuestionnaire: true // 允许未完成问卷的用户访问
      }
    },
    {
      path: '/questionnaire/results/:responseId',
      name: 'QuestionnaireResults',
      component: () => import('@/views/QuestionnaireResultsPage.vue'),
      meta: {
        title: 'Carbon Footprint Results',
        requiresAuth: false,
        allowWithoutQuestionnaire: true // 允许未完成问卷的用户访问
      }
    },
    {
      path: '/carbon-footprint',
      name: 'CarbonFootprint',
      component: () => import('@/views/CarbonFootprintPage.vue'),
      meta: {
        title: 'My Carbon Footprint',
        requiresAuth: true
      }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardPage,
      meta: {
        title: 'Dashboard',
        requiresAuth: true
      }
    },
    {
      path: '/start-projects',
      name: 'StartProjects',
      component: () => import('@/views/StartProjectsPage.vue'),
      meta: {
        title: 'Start Projects',
        requiresAuth: true
      }
    },
    {
      path: '/carbon-credit-buyers',
      name: 'CarbonCreditBuyers',
      component: () => import('@/views/CarbonCreditBuyersPage.vue'),
      meta: {
        title: 'Carbon Credit Buyers',
        requiresAuth: true
      }
    },
    {
      path: '/carbon-credit-buyer-details/:buyerId',
      name: 'CarbonCreditBuyerDetails',
      component: () => import('@/views/CarbonCreditBuyerDetailsPage.vue'),
      meta: {
        title: 'Buyer Details',
        requiresAuth: true
      }
    },
    {
      path: '/carbon-credit-sale/:buyerId',
      name: 'CarbonCreditSale',
      component: () => import('@/views/CarbonCreditSalePage.vue'),
      meta: {
        title: 'Sell Carbon Credits',
        requiresAuth: true
      }
    },
    {
      path: '/sale-orders',
      name: 'SaleOrders',
      component: () => import('@/views/SaleOrdersPage.vue'),
      meta: {
        title: 'Sale Orders',
        requiresAuth: true
      }
    },
    {
      path: '/sale-orders/:id',
      name: 'SaleOrderDetail',
      component: () => import('@/views/SaleOrderDetailPage.vue'),
      meta: {
        title: 'Sale Order Details',
        requiresAuth: true
      }
    },

    {
      path: '/simple-debug',
      name: 'SimpleDebug',
      component: () => import('@/views/SimpleDebugPage.vue'),
      meta: {
        title: 'Simple Debug Tool',
        requiresAuth: true,
        allowWithoutQuestionnaire: true
      }
    },

    {
      path: '/projects',
      name: 'ProjectTypes',
      component: () => import('@/views/ProjectTypesPage.vue'),
      meta: {
        title: 'Project Types',
        requiresAuth: false
      }
    },
    {
      path: '/projects/type/:typeId',
      name: 'ProjectList',
      component: () => import('@/views/ProjectListPage.vue'),
      meta: {
        title: 'Projects',
        requiresAuth: false
      }
    },
    {
      path: '/projects/regular/:id',
      name: 'RegularProjectDetail',
      component: () => import('@/views/RegularProjectDetailPage.vue'),
      meta: {
        title: 'Project Details',
        requiresAuth: false
      }
    },
    {
      path: '/projects/pre-sale/:id',
      name: 'PreSaleProjectDetail',
      component: () => import('@/views/PreSaleProjectDetailPage.vue'),
      meta: {
        title: 'Pre-sale Project Details',
        requiresAuth: false
      }
    },
    // 碳信用交易相关路由
    {
      path: '/buyers',
      name: 'BuyerList',
      component: () => import('@/views/BuyerListPage.vue'),
      meta: {
        title: 'Carbon Credit Buyers',
        requiresAuth: true
      }
    },
    {
      path: '/trading/:buyerId',
      name: 'Trading',
      component: () => import('@/views/TradingPage.vue'),
      meta: {
        title: 'Carbon Credit Trading',
        requiresAuth: true
      }
    },
    {
      path: '/transactions/success/:transactionId',
      name: 'TransactionSuccess',
      component: () => import('@/views/TransactionSuccessPage.vue'),
      meta: {
        title: 'Transaction Success',
        requiresAuth: true
      }
    },
    {
      path: '/transactions/history',
      name: 'TransactionHistory',
      component: () => import('@/views/TransactionHistoryPage.vue'),
      meta: {
        title: 'Transaction History',
        requiresAuth: true
      }
    },

    // 预售意向管理路由
    {
      path: '/presale-intentions',
      name: 'PresaleIntentions',
      component: () => import('@/views/PresaleIntentionsPage.vue'),
      meta: {
        title: 'My Presale Intentions',
        requiresAuth: true
      }
    },
    {
      path: '/presale-intentions/:id',
      name: 'PresaleIntentionDetail',
      component: () => import('@/views/PresaleIntentionDetailPage.vue'),
      meta: {
        title: 'Presale Intention Detail',
        requiresAuth: true
      }
    },

    // 资产管理路由
    {
      path: '/asset-management',
      name: 'AssetManagement',
      component: () => import('@/views/AssetManagementPage.vue'),
      meta: {
        title: 'Asset Management',
        requiresAuth: true
      }
    },

    // 用户个人中心相关路由
    {
      path: '/personal-center',
      name: PERSONAL_CENTER_ROUTES.PERSONAL_CENTER,
      component: () => import('@/views/PersonalCenterPage.vue'),
      meta: {
        title: 'Personal Center',
        requiresAuth: true,
        description: '个人中心主页面，显示会员信息、钱包余额、碳信用等',
        group: 'personal',
        icon: 'user'
      }
    },
    {
      path: '/order-record',
      name: PERSONAL_CENTER_ROUTES.ORDER_RECORD,
      component: () => import('@/views/OrderRecordPage.vue'),
      meta: {
        title: 'Order Record',
        requiresAuth: true,
        description: '订单记录页面，显示项目参与记录和交易统计',
        group: 'personal',
        icon: 'receipt'
      }
    },
    {
      path: '/account-settings',
      name: PERSONAL_CENTER_ROUTES.ACCOUNT_SETTINGS,
      component: () => import('@/views/AccountSettingsPage.vue'),
      meta: {
        title: 'Account Settings',
        requiresAuth: true,
        description: '账号设定页面，管理个人信息、收款地址和密码设置',
        group: 'personal',
        icon: 'settings'
      }
    },
    {
      path: '/asset-detail',
      name: 'AssetDetailPage',
      component: () => import('@/views/AssetDetailPage.vue'),
      meta: {
        title: 'Asset Analysis',
        requiresAuth: true,
        description: '资产详情分析页面，显示资产明细、变化历史和统计图表',
        group: 'personal',
        icon: 'trending-up'
      }
    },






    {
      path: '/user/chat',
      name: 'UserChat',
      component: () => import('@/views/user/ChatPage.vue'),
      meta: {
        title: 'Customer Support',
        requiresAuth: true
      }
    },
    {
      path: '/about',
      name: 'AboutUs',
      component: () => import('@/views/AboutUsPage.vue'),
      meta: {
        title: 'About Us',
        requiresAuth: false,
        description: '关于我们页面，介绍机构使命和气候变化影响',
        group: 'main'
      }
    },
    {
      path: '/membership',
      name: 'Membership',
      component: () => import('@/views/MembershipPage.vue'),
      meta: {
        title: 'Membership',
        requiresAuth: false,
        description: 'Membership Tiers & Benefits',
        group: 'main'
      }
    },
    
    // 404 页面 - 必须放在最后
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundPage.vue'),
      meta: {
        title: 'Page Not Found',
        requiresAuth: false,
        description: '页面未找到',
        hideInNav: true
      }
    }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // 路由滚动行为
  scrollBehavior(to, _from, savedPosition) {
    // 如果有保存的位置（浏览器前进/后退），恢复到该位置
    if (savedPosition) {
      return savedPosition
    }
    // 如果有锚点，滚动到锚点
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    // 否则滚动到顶部
    return { top: 0, behavior: 'smooth' }
  }
})

// 使用模块化的路由守卫
router.beforeEach(mainNavigationGuard)

// 路由導航完成後追蹤頁面訪問
router.afterEach((to) => {
  activityTracker.trackPageVisit(to.fullPath)
})

// 路由拦截器暂时禁用
// import { setupRouteInterceptor } from '@/utils/routeInterceptor'
// setupRouteInterceptor(router)

// 路由错误处理
router.onError((error) => {
  console.error('[Router] Navigation error:', error)
})

// 导出路由实例和相关工具
export default router
export * from './types'
export * from './utils'
export * from './config'
export * from './guards'
